import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, RefreshCw } from "lucide-react";

export default function CalendarEvents() {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedWs, setSelectedWs] = useState<string>("");
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get("/api/v1/workspaces").then((ws) => {
      setWorkspaces(ws);
      if (ws.length > 0) setSelectedWs(ws[0].id.toString());
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedWs) return;
    setLoading(true);
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();
    api.get(`/api/v1/events?workspaceId=${selectedWs}&from=${from}&to=${to}`)
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedWs]);

  const handleSync = async () => {
    if (!selectedWs) return;
    setSyncing(true);
    try {
      await api.post(`/api/v1/sync/trigger`, { workspaceId: parseInt(selectedWs) });
      toast.success("Sync triggered! Events will update shortly.");
    } catch (e: any) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  };

  // Group events by date
  const grouped = events.reduce((acc: Record<string, any[]>, e) => {
    const date = e.start_time?.slice(0, 10) || "unknown";
    if (!acc[date]) acc[date] = [];
    acc[date].push(e);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Events</h1>
        <div className="flex items-center gap-3">
          {workspaces.length > 0 && (
            <select value={selectedWs} onChange={(e) => setSelectedWs(e.target.value)} className="px-3 py-2 rounded-lg border border-input bg-background text-sm">
              {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <button onClick={handleSync} disabled={syncing} className="px-3 py-2 rounded-lg border border-input bg-background text-sm hover:bg-accent flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sync
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1,2,3].map(i=><div key={i} className="h-20 bg-muted rounded-xl"/>)}</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">No events found</p>
          <p className="text-sm text-muted-foreground mt-1">Connect a calendar and sync to see events here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([date, dayEvents]) => (
            <div key={date}>
              <h2 className="text-sm font-semibold text-muted-foreground mb-2">{formatDate(date + "T00:00:00")}</h2>
              <div className="space-y-2">
                {dayEvents.map((e: any) => (
                  <div key={e.id} className="bg-card rounded-xl border border-border p-4 flex items-start gap-3">
                    <div className="w-1 h-12 rounded-full bg-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">{e.title || "Untitled"}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(e.start_time)} — {formatTime(e.end_time)}</span>
                        {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</span>}
                      </div>
                      {e.provider && <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{e.provider}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
